import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { EventRecord } from "@/shared/domain/events";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { PaginatedResponse } from "@/shared/http";

export function useDatasetEvents(datasetId: string, resourceOwnerId: string) {
  const { datasetService } = useDomainServices();

  return useQuery<EventRecord[], Error>({
    queryKey: eventQueryKeys.listsScope(resourceOwnerId, {
      type: "dataset",
      id: datasetId,
    }),
    queryFn: async ({ signal }) => {
      let items: EventRecord[] = [];
      let nextToken: string | undefined = undefined;

      do {
        const data: PaginatedResponse<EventRecord> =
          await datasetService.getEventsForDataset(datasetId, {
            pageToken: nextToken,
            limit: 1000,
            abortSignal: signal,
          });

        items = items.concat(data.items);
        nextToken = data.next_token ?? undefined;
      } while (nextToken);

      return items;
    },
  });
}

export function useDeleteDatasetEvents(
  datasetId: string,
  resourceOwnerId: string,
  onDeleteEvents: (events: EventRecord["event_id"][]) => void,
) {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();
  return useMutation<void, Error, EventRecord["event_id"][]>({
    mutationFn: (eventIds) =>
      events.deleteEvents(eventIds, { resourceOwnerId }),
    onSuccess: (_, eventIds) => {
      onDeleteEvents(eventIds);
      // Optimistic surgical removal from this dataset's cached list so
      // the UI updates immediately.
      queryClient.setQueryData(
        eventQueryKeys.listsScope(resourceOwnerId, {
          type: "dataset",
          id: datasetId,
        }),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }
          return oldData.filter((event) => !eventIds.includes(event.event_id));
        },
      );
      // The deleted events may also appear in other list views (org
      // page, collection tabs). Mark the list ladder stale for next
      // mount. `refetchType: "none"` avoids re-fetching the currently-
      // active dataset list, which we just updated via `setQueryData`
      // above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(resourceOwnerId),
        refetchType: "none",
      });
      // Workspace views may also include these events.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(resourceOwnerId),
      });
    },
  });
}
